import React, { useMemo } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  useCollectionBinding,
  useContentToken,
  useEditMode,
  RichTextContent,
  EditableToken,
} from '@vibesites/runtime/react';
import { useScrollReveal, useStaggerReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { useSiteCtaStyles } from '../hooks/useSiteCtaStyles';
import { EyeOff } from 'lucide-react';
import marbleBgUrl from '../assets/marble-bg.jpg';

interface Floorplan {
  floorplan_id?: string | null;
  floorplan_name?: string | null;
  floorplan_image_url?: string | null;
  floorplan_beds_min?: string | null;
  floorplan_beds_max?: string | null;
  floorplan_baths_min?: string | null;
  floorplan_sqft_min?: string | null;
  floorplan_sqft_max?: string | null;
  floorplan_price_min?: string | null;
  floorplan_price_max?: string | null;
  floorplan_available_units?: string | null;
}

// ============================================================================
// Bedroom category helpers
// ============================================================================

interface BedroomCategory {
  key: string;
  label: string;
  digit: string;
  param: string;
}

const BED_ORDER = ['studio', 'one', 'two', 'three', 'four', 'five'];

function bedCountToKey(bedsRaw: string | null | undefined): string {
  if (!bedsRaw) return 'studio';
  const trimmed = bedsRaw.trim().toLowerCase();
  if (trimmed === '0' || trimmed === 'studio' || trimmed === 'std') return 'studio';
  const num = parseInt(trimmed, 10);
  if (isNaN(num) || num <= 0) return 'studio';
  const names = ['studio', 'one', 'two', 'three', 'four', 'five'];
  return names[num] || `${num}`;
}

const WORD_NUMBERS: Record<string, string> = {
  studio: 'Studio',
  one: 'One',
  two: 'Two',
  three: 'Three',
  four: 'Four',
  five: 'Five',
};

/** Word-based category label, e.g. "One Bedroom", "Two Bedrooms", "Studio". */
function keyToWordLabel(key: string): string {
  if (key === 'studio') return 'Studio';
  const word = WORD_NUMBERS[key] || key;
  const noun = key === 'one' ? 'Bedroom' : 'Bedrooms';
  return `${word} ${noun}`;
}

/** Big display digit — "S" for studio, otherwise the bedroom count. */
function keyToDigit(key: string): string {
  if (key === 'studio') return 'S';
  const idx = BED_ORDER.indexOf(key);
  return idx > 0 ? String(idx) : key;
}

/**
 * Parse a price string ("1500", "$1,500", "1,500.00") to a positive integer,
 * or null if it isn't a usable number. AptSync/property data often stores
 * prices as strings with commas or currency symbols.
 */
function parsePrice(raw: string | null | undefined): number | null {
  if (!raw) return null;
  const cleaned = raw.replace(/[^0-9.]/g, '');
  if (!cleaned) return null;
  const n = Math.round(parseFloat(cleaned));
  return isFinite(n) && n > 0 ? n : null;
}

/** Lowest `floorplan_price_min` across the loaded floorplans, or null. */
function minStartingPrice(floorplans: Floorplan[]): number | null {
  let min: number | null = null;
  for (const plan of floorplans) {
    const price = parsePrice(plan.floorplan_price_min);
    if (price != null && (min == null || price < min)) min = price;
  }
  return min;
}

const PRICE_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

function groupFloorplansByBeds(floorplans: Floorplan[]): BedroomCategory[] {
  const groups: Record<string, Floorplan[]> = {};

  for (const plan of floorplans) {
    const key = bedCountToKey(plan.floorplan_beds_min);
    if (!groups[key]) groups[key] = [];
    groups[key].push(plan);
  }

  const sorted = Object.keys(groups).sort((a, b) => {
    const ai = BED_ORDER.indexOf(a);
    const bi = BED_ORDER.indexOf(b);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

  return sorted.map((key) => ({
    key,
    label: keyToWordLabel(key),
    digit: keyToDigit(key),
    param: key,
  }));
}

// ============================================================================
// Category column sub-component
// ============================================================================

function CategoryColumn({ cat }: { cat: BedroomCategory }) {
  return (
    <a
      data-reveal
      href={`/floor-plans?bedroom=${cat.param}`}
      className="reveal-fade-up fp-col group flex flex-col items-center text-center no-underline"
    >
      <span className="fp-col-digit leading-none text-[var(--vibe-color-text)] transition-transform duration-300 group-hover:-translate-y-1">
        {cat.digit}
      </span>
      <span className="fp-col-label mt-6 uppercase tracking-wide text-[var(--vibe-color-text)] whitespace-nowrap">
        {cat.label}
      </span>
      {/* Uniform underline: rendered as a separate fixed-width bar so every
          category's accent is the same length regardless of label text
          (STUDIO vs TWO BEDROOMS). */}
      <span className="fp-col-underline" aria-hidden="true" />
    </a>
  );
}

// ============================================================================
// Component
// ============================================================================

export function FloorplansList() {
  const floorplans = useCollectionBinding<Floorplan>('floorplanslist.items', {
    collection: 'floorplans',
  });

  const categories = useMemo(
    () => (floorplans?.length ? groupFloorplansByBeds(floorplans) : []),
    [floorplans],
  );

  // "Starting at $X,XXX" derived from the lowest floorplan min price. Stays in
  // sync with editor overrides on the floorplans collection above.
  const startingPrice = useMemo(
    () => (floorplans?.length ? minStartingPrice(floorplans) : null),
    [floorplans],
  );
  const startingPriceLabel = useContentToken('floorplanslist.starting_price_label');
  const formattedStartingPrice = startingPrice != null ? PRICE_FORMATTER.format(startingPrice) : null;

  useRevealStyles();
  useSiteCtaStyles();
  const headerRef = useScrollReveal<HTMLDivElement>();
  const gridRef = useStaggerReveal<HTMLDivElement>({ staggerMs: 120 });
  const editMode = useEditMode();
  const isEditMode = editMode?.isEditMode ?? false;

  // Hide when there is at most one bedroom category — the "View All" CTA alone
  // (or paired with a single category) is not useful enough to show the section.
  // In the editor, show a placeholder so authors can see why it is missing.
  if (categories.length <= 1) {
    if (!isEditMode) return null;
    return (
      <section
        className="py-6 md:py-8 bg-[var(--vibe-color-background)]"
        aria-label="Floor plans list hidden"
      >
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className="rounded-sm border-2 border-dashed px-5 py-6 text-center"
            style={{
              borderColor: '#9a9a9a',
              backgroundColor: '#f7f7f7',
              color: '#595959',
            }}
            role="status"
          >
            <div className="flex flex-col items-center gap-2">
              <EyeOff
                className="w-5 h-5 shrink-0"
                style={{ color: '#6b6b6b' }}
                aria-hidden="true"
              />
              <p className="font-heading text-base font-semibold m-0" style={{ color: '#3d3d3d' }}>
                Section hidden on live site
              </p>
              <p className="font-body text-sm leading-snug m-0 max-w-md">
                Only one bedroom layout is available. This section appears when
                there are two or more.
              </p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // On sm+ we render each category into an equal-width CSS grid track so
  // the big digits (S / 1 / 2 …) sit at evenly spaced centers regardless
  // of how wide each label happens to be ("STUDIO" vs "TWO BEDROOMS").
  // The column count is data-driven, so we drive it through a CSS var
  // instead of a dynamic Tailwind class (JIT can't see runtime strings).
  // On mobile, 4+ categories switch to a 2-up grid (see .fp-grid--many).
  const gridStyle = {
    '--fp-cols': categories.length || 1,
  } as React.CSSProperties;
  const manyOnMobile = categories.length > 3;

  return (
    <section className="relative overflow-hidden py-16 md:py-24 bg-[var(--vibe-color-background)]">
      {/* White marble texture (close-up slab) — full-bleed behind content. */}
      <img
        src={marbleBgUrl}
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-0 h-full w-full object-cover"
      />
      <div
        className="pointer-events-none absolute inset-0 z-0 bg-white/70"
        aria-hidden="true"
      />
      <div className="relative z-[1] max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={headerRef} className="reveal-fade-up text-center mb-12 md:mb-20">
          <RichTextContent
            path="floorplanslist.title"
            label="Title"
            as="h2"
            className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold uppercase text-[var(--vibe-color-text)] mb-5 leading-tight tracking-wide"
          />
          <p className="font-body text-base sm:text-lg text-[var(--vibe-color-text)] max-w-2xl mx-auto leading-relaxed [&_a]:text-[var(--vibe-color-text)] [&_a]:underline [&_a]:decoration-[var(--vibe-color-primary)] [&_a]:underline-offset-2 [&_a:hover]:decoration-2">
            <RichTextContent
              path="floorplanslist.subtitle"
              label="Subtitle"
              as="span"
            />
            {/* "Starting at $X" intentionally hidden for now. Flip this guard
                back to `formattedStartingPrice` to restore the price label. */}
            {false && formattedStartingPrice && (
              <>
                {' '}
                <EditableToken
                  path="floorplanslist.starting_price_label"
                  label="Starting Price Label"
                  as="span"
                >
                  {startingPriceLabel}
                </EditableToken>{' '}
                <span className="font-bold text-[var(--vibe-color-primary)]">
                  {formattedStartingPrice}
                </span>
              </>
            )}
          </p>
        </div>

        <div
          ref={gridRef}
          style={gridStyle}
          className={`fp-grid grid gap-y-10 sm:gap-x-6 md:gap-x-10 lg:gap-x-12 mb-16 md:mb-20 mx-auto${manyOnMobile ? ' fp-grid--many' : ''}`}
        >
          {categories.map((cat) => (
            <CategoryColumn key={cat.key} cat={cat} />
          ))}
        </div>

        <div className="flex justify-center">
          <a
            data-reveal
            href="/floor-plans"
            className="reveal-fade-up site-cta site-cta--secondary fp-view-all-cta"
            // Width-matched to common footer CTAs so the button family reads
            // as one system across the theme rather than sizing to label text.
            style={{ width: '100%', maxWidth: '260px' }}
          >
            View All Layouts
          </a>
        </div>
      </div>

      <style>{`
        /* Mobile default: single column. With 4+ categories (.fp-grid--many),
           use a 2-up grid so the list doesn't get overly tall. Type scale
           for the 2-up case is after the base digit/label rules below. */
        .fp-grid {
          grid-template-columns: 1fr;
        }
        @media (max-width: 639px) {
          .fp-grid.fp-grid--many {
            grid-template-columns: repeat(2, minmax(0, 1fr));
            column-gap: 0.75rem;
            row-gap: 2rem;
          }
        }

        /* Equal-width tracks on sm+ so every big digit lands at the same
           relative x-position within its cell. Without this, columns take
           their natural content width (label-driven) and the digits end up
           unevenly spaced. */
        @media (min-width: 640px) {
          .fp-grid {
            grid-template-columns: repeat(var(--fp-cols, 3), minmax(0, 1fr));
            /* Keep the row visually anchored — cap width so 2- or 3-item
               layouts don't stretch across the full 1100px container. */
            max-width: min(100%, calc(var(--fp-cols, 3) * 14rem));
          }
        }

        /* ── Icon (big bedroom-count digit) ────────────────────────────
           Aion type spec: Montserrat, Normal (400), 120px. Hover state
           uses the same face, weight, and size — only the transform /
           color transitions change. clamp() keeps the digit legible on
           narrow viewports while topping out at 120px on desktop. */
        .fp-col-digit {
          font-family: var(--vibe-font-heading, sans-serif);
          font-weight: 400;
          font-size: clamp(64px, 14vw, 120px);
        }
        @media (min-width: 1024px) {
          .fp-col-digit {
            font-size: 120px;
          }
        }
        .fp-col:hover .fp-col-digit {
          font-family: var(--vibe-font-heading, sans-serif);
          font-weight: 400;
          font-size: clamp(64px, 14vw, 120px);
        }
        @media (min-width: 1024px) {
          .fp-col:hover .fp-col-digit {
            font-size: 120px;
          }
        }

        /* ── Floor Plan Name (label) ───────────────────────────────────
           Aion type spec: Montserrat, Normal (400), 18px / 42px
           line-height. The 10px accent bar is rendered as a sibling
           (.fp-col-underline) below so its width is uniform across
           categories rather than sized to the label text. The label
           typography re-declares on hover to guarantee the hover state
           matches the resting state. */
        .fp-col-label {
          display: inline-block;
          font-family: var(--vibe-font-body, sans-serif);
          font-weight: 400;
          font-size: 18px;
          line-height: 42px;
          transition: color 0.25s ease;
        }
        .fp-col:hover .fp-col-label,
        .fp-col:focus-visible .fp-col-label {
          font-family: var(--vibe-font-body, sans-serif);
          font-weight: 400;
          font-size: 18px;
        }

        /* ── Uniform underline ──────────────────────────────────────────
           Fixed-width accent bar rendered independently of the label so
           every column has the same-length underline. Matches the
           previous 10px height / #d4d3d3 color from the label border. */
        .fp-col-underline {
          display: block;
          width: 140px;
          height: 10px;
          background-color: #d4d3d3;
          margin-top: 12px;
        }

        /* Tablet: slightly tighter columns / type so 4-up rows don't
           crowd the padded content edges (desktop keeps the full scale). */
        @media (min-width: 640px) and (max-width: 1023px) {
          .fp-grid {
            max-width: min(100%, calc(var(--fp-cols, 3) * 11.5rem));
            gap: 1.25rem 1rem;
          }
          .fp-col-digit,
          .fp-col:hover .fp-col-digit {
            font-size: clamp(52px, 9vw, 88px);
          }
          .fp-col-label,
          .fp-col:hover .fp-col-label,
          .fp-col:focus-visible .fp-col-label {
            font-size: 15px;
            line-height: 32px;
          }
          .fp-col-underline {
            width: 100px;
            height: 8px;
            margin-top: 8px;
          }
        }

        /* Mobile 2-up (4+ categories): slightly smaller type so labels
           like "THREE BEDROOMS" fit half-width columns. */
        @media (max-width: 639px) {
          .fp-grid.fp-grid--many .fp-col-digit,
          .fp-grid.fp-grid--many .fp-col:hover .fp-col-digit {
            font-size: clamp(48px, 18vw, 72px);
          }
          .fp-grid.fp-grid--many .fp-col-label,
          .fp-grid.fp-grid--many .fp-col:hover .fp-col-label,
          .fp-grid.fp-grid--many .fp-col:focus-visible .fp-col-label {
            font-size: 13px;
            line-height: 28px;
          }
          .fp-grid.fp-grid--many .fp-col-underline {
            width: 88px;
            height: 8px;
            margin-top: 8px;
          }
        }

        /* ── "View All Layouts" CTA typography override ─────────────────
           Site CTAs default to a heavier uppercase treatment. In this
           section the button should read closer to the Comfy reference
           (normal weight), so we scope the override to this button only. */
        .fp-view-all-cta {
          font-family: var(--vibe-font-heading, var(--vibe-font-body, sans-serif));
          font-weight: 400;
          letter-spacing: 0.04em;
          text-transform: none;
          font-size: 1.1rem;
          border-radius: 10px;
          padding: 1.1rem 1.5rem;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FloorplansList',
  displayName: 'Floor Plans List',
  tokens: {
    'floorplanslist.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Make Yourself at Home</p>',
    },
    'floorplanslist.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue:
        '<p>Please select your desired layout below or browse our <a href="/gallery">photo gallery</a>.</p>',
    },
    'floorplanslist.starting_price_label': {
      label: 'Starting Price Label',
      type: 'text',
      defaultValue: 'Starting at',
    },
  },
  collectionBindings: {
    'floorplanslist.items': {
      label: 'Floor Plans',
      collection: 'floorplans',
    },
  },
};

(FloorplansList as any).contentSchema = contentSchema;

export default FloorplansList;
